/**
 * Authentication Service
 *
 * Handles Google OAuth login, token management, and user context
 */

import apiClient from "./api-client";
import type { UserContext } from "@/types";

class AuthService {
  /**
   * Initiate Google OAuth login
   * Redirects to backend OAuth endpoint
   *
   * @param teamId - Optional team ID to log into a specific team (will be validated)
   */
  login(teamId?: string): void {
    const redirectUri = `${window.location.origin}/auth/callback`;
    const apiBaseUrl =
      import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

    // Build login URL with optional team_id
    let loginUrl = `${apiBaseUrl}/auth/login?redirect_uri=${encodeURIComponent(redirectUri)}`;

    // Add team_id if provided (backend will validate user membership)
    if (teamId) {
      loginUrl += `&team_id=${encodeURIComponent(teamId)}`;
    }

    // Redirect to backend OAuth endpoint
    window.location.href = loginUrl;
  }

  /**
   * Handle OAuth callback
   * Exchange authorization code for JWT token
   */
  async handleCallback(code: string): Promise<UserContext> {
    try {
      // Include the same redirect_uri that was used in the initial auth request
      // This MUST match for Google OAuth to accept the token exchange
      const redirectUri = `${window.location.origin}/auth/callback`;

      // Backend returns {token, user} format
      const response = await apiClient.postRaw<{
        token: string;
        user: UserContext;
      }>("/auth/callback", {
        code,
        redirect_uri: redirectUri, // Send redirect_uri to backend
      });

      // Extract token and user from response
      const { token, user } = response.data;

      // Store token
      localStorage.setItem("auth_token", token);

      // Store complete user context from backend (includes all fields)
      localStorage.setItem("user_context", JSON.stringify(user));

      console.log("Auth callback successful:", user);

      return user;
    } catch (error) {
      console.error("OAuth callback error:", error);
      throw error;
    }
  }

  /**
   * Get current user context
   */
  async getCurrentUser(): Promise<UserContext> {
    try {
      // Use getRaw since /auth/me returns data directly
      const response = await apiClient.getRaw<UserContext>("/auth/me");
      const user = response.data;

      console.log("getCurrentUser received:", user);

      // Update stored user context
      localStorage.setItem("user_context", JSON.stringify(user));

      return user;
    } catch (error) {
      console.error("Get current user error:", error);
      throw error;
    }
  }

  /**
   * Switch team/project context
   */
  async switchContext(
    teamId?: string,
    projectId?: string,
  ): Promise<UserContext> {
    try {
      const user = await apiClient.post<UserContext>("/auth/switch-context", {
        team_id: teamId,
        project_id: projectId,
      });

      // Update stored user context
      localStorage.setItem("user_context", JSON.stringify(user));

      return user;
    } catch (error) {
      console.error("Switch context error:", error);
      throw error;
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await apiClient.post<{ access_token: string }>(
        "/auth/refresh",
        { refresh_token: refreshToken },
      );

      // Store new token
      localStorage.setItem("auth_token", response.access_token);

      return response.access_token;
    } catch (error) {
      console.error("Token refresh error:", error);
      this.logout();
      throw error;
    }
  }

  /**
   * Logout user
   * Calls backend logout endpoint for audit logging, then clears local storage
   */
  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint for audit trail
      await apiClient.post("/auth/logout", {});
      console.log("Backend logout successful");
    } catch (error) {
      console.error("Backend logout error:", error);
      // Continue with local cleanup even if backend call fails
    } finally {
      // Clear all auth data
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_context");

      // Redirect to login
      window.location.href = "/login";
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem("auth_token");
    return !!token;
  }

  /**
   * Get stored user context (without API call)
   */
  getStoredUser(): UserContext | null {
    const userStr = localStorage.getItem("user_context");
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
export default authService;
